import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import {
  XMarkIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import itraceSerializationService from '../services/itraceSerializationService';

// Print rules that apply only while this window is open. The window is rendered
// straight under <body> (see createPortal below), so everything else on the page
// can be hidden and the labels flow over as many printed pages as they need.
const QR_PRINT_CSS = `
@media print {
  html, body { height: auto !important; overflow: visible !important; }
  body > *:not(.itrace-qr-print-root) { display: none !important; }
  .itrace-qr-print-root { position: static !important; display: block !important; padding: 0 !important; background: transparent !important; }
  .itrace-qr-print-sheet { display: block !important; width: auto !important; max-width: none !important; max-height: none !important; overflow: visible !important; }
  .itrace-qr-print-body { overflow: visible !important; }
}
`;

/**
 * QR Viewer modal — fetches and renders the actual QR codes linked to a SIRIM cycle.
 *
 * Flow: cycle → plant batch (match via description "Cycle: <id>") → commission → codes.
 * If no commission exists yet, the modal tells the user to open the iTrace portal to
 * create one.
 */
const ITraceQRViewerModal = ({ isOpen, onClose, cycle }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [portalError, setPortalError] = useState('');
  const printRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !cycle?.id) return;
    const load = async () => {
      setLoading(true);
      setError('');
      setPortalError('');
      setResult(null);
      try {
        const data = await itraceSerializationService.getLabelsForCycle(cycle.id);
        setResult(data);
      } catch (e) {
        console.error('QR viewer load error:', e);
        setError(e.message || 'Failed to load QR codes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, cycle?.id]);

  const openPortal = async () => {
    setPortalError('');
    try {
      // The new tab is opened inside this click (before the first await) so
      // pop-up blockers allow it; it is pointed at the portal once the token arrives.
      await itraceSerializationService.openPortalInNewTab();
    } catch (e) {
      // Kept apart from `error` so the QR codes stay on screen.
      setPortalError(`Could not open the iTrace portal. ${e.message || ''}`.trim());
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // no-op
    }
  };

  // Trigger the browser's print dialog, which doubles as "Save as PDF" on all modern OSes.
  const printQRs = () => {
    window.print();
  };

  // Download all QR codes as individual PNG files (zipped via rendering to canvas one at a time).
  // Simpler approach: download the first as example, and offer per-card download.
  const downloadAsPng = (traceUrl, filename) => {
    const canvas = document.getElementById(`qr-canvas-${filename}`);
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${filename}.png`;
    link.click();
  };

  if (!isOpen) return null;

  const codes = result?.codes || [];
  const commission = result?.commission;
  const plantBatch = result?.plantBatch;

  return createPortal(
    <div className="itrace-qr-print-root fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:relative print:bg-transparent print:p-0 print:inset-auto">
      <style>{QR_PRINT_CSS}</style>
      <div
        className="itrace-qr-print-sheet bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col print:max-w-none print:max-h-none print:w-auto print:rounded-none print:shadow-none"
        ref={printRef}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:hidden">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              QR Codes — {cycle?.name_season || 'Cycle'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Generated by iTrace from the Plant Batch linked to this cycle
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block px-6 py-4 border-b">
          <h2 className="text-xl font-bold">{cycle?.name_season}</h2>
          <p className="text-sm text-gray-700">
            {commission?.commissionName} · {codes.length} labels ·
            Plant Batch EPC: <code className="text-xs">{plantBatch?.batchCode}</code>
          </p>
        </div>

        {/* Toolbar */}
        {!loading && codes.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap print:hidden">
            <button onClick={printQRs} className="btn-secondary text-sm flex items-center gap-1.5">
              <PrinterIcon className="h-4 w-4" />
              Print / Save as PDF
            </button>
            <button onClick={openPortal} className="btn-secondary text-sm flex items-center gap-1.5">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Open in iTrace Portal
            </button>
            <div className="flex-1" />
            <span className="text-sm text-gray-600">
              {codes.length} label{codes.length === 1 ? '' : 's'} ·
              Commission: <strong>{commission?.commissionName}</strong>
            </span>
          </div>
        )}

        {/* Portal error — outside the scrolling body so it stays in view */}
        {portalError && (
          <div className="mx-6 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 print:hidden">
            {portalError}
          </div>
        )}

        {/* Body */}
        <div className="itrace-qr-print-body flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
              Loading QR codes from iTrace...
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Could not load QR codes.</strong>
                  <p className="mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && !plantBatch && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-sm text-yellow-900">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>No Plant Batch found for this cycle in iTrace.</strong>
                  <p className="mt-1">
                    Either the cycle was ended <em>before</em> SIRIM started sending harvest data
                    to iTrace, or its harvest was never sent to iTrace. Open the iTrace portal to
                    check the batch list and create / view a Commission manually.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && plantBatch && !commission && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-sm text-blue-900">
              <strong>Plant Batch exists but no Commission linked yet.</strong>
              <p className="mt-1">
                The harvest is in iTrace as batch{' '}
                <code className="bg-white px-1 py-0.5 rounded border text-xs">
                  {plantBatch.batchCode}
                </code>{' '}
                but no one has created a Commission for it yet. QR codes are only generated
                after a Commission is created.
              </p>
              <button
                onClick={openPortal}
                className="btn-primary mt-3 text-sm flex items-center gap-1.5"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open iTrace to create a Commission
              </button>
            </div>
          )}

          {!loading && !error && codes.length > 0 && (
            <>
              {plantBatch?.batchCode && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 mb-3 print:hidden">
                  <strong>Plant Batch EPC:</strong>{' '}
                  <code className="bg-white px-1.5 py-0.5 rounded border break-all">
                    {plantBatch.batchCode}
                  </code>
                  {commission?.commissionName && (
                    <span className="ml-3">
                      <strong>Commission:</strong> {commission.commissionName}
                    </span>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mb-3 print:hidden">
                <strong>What the status badges mean:</strong>
                <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                  <li><code className="bg-white px-1 rounded">CREATED</code> — label generated by commission, not printed yet</li>
                  <li><code className="bg-white px-1 rounded">PRINTED</code> — printed via iTrace portal, awaiting field validation</li>
                  <li><code className="bg-white px-1 rounded">VERIFIED</code> — physically scanned + validated; consumer scans now resolve</li>
                </ul>
                <p className="mt-2">
                  A QR only resolves to product info <strong>after</strong> it reaches{' '}
                  <code className="bg-white px-1 rounded">VERIFIED</code>. Open the iTrace
                  portal to print + validate via the Serialization module's scan flow.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4 print:hidden">
                <strong>Scan behaviour (verified Jul 2026):</strong>{' '}
                <code className="bg-white px-1 rounded">VERIFIED</code> codes now resolve to full
                product info on the consumer PWA — the earlier dev-sync gap ("102 product not
                found" for everything) has been fixed upstream.{' '}
                <code className="bg-white px-1 rounded">CREATED</code> /{' '}
                <code className="bg-white px-1 rounded">PRINTED</code> codes still show
                <em> "102 product not found"</em> until they are validated to VERIFIED in the
                iTrace portal.
              </div>
            </>
          )}

          {!loading && !error && codes.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3">
              {codes.map((c, idx) => (
                <div
                  key={c.traceCode}
                  className="border border-gray-200 rounded-lg p-3 flex flex-col items-center gap-2 bg-white print:break-inside-avoid"
                >
                  <div className="text-xs text-gray-500 self-start">
                    #{String(idx + 1).padStart(2, '0')}
                  </div>
                  {/* Visible SVG for crisp screen rendering */}
                  <QRCodeSVG
                    value={c.traceUrl}
                    size={160}
                    includeMargin
                    className="border border-gray-100 rounded"
                  />
                  {/* Hidden canvas so we can download per-QR PNG without re-encoding */}
                  <div className="hidden">
                    <QRCodeCanvas
                      id={`qr-canvas-${c.traceCode}`}
                      value={c.traceUrl}
                      size={400}
                      includeMargin
                    />
                  </div>
                  <div className="text-xs font-mono text-gray-700 break-all text-center">
                    {c.traceCode}
                  </div>
                  <div className={`text-xs px-2 py-0.5 rounded ${
                    c.status === 'CREATED' ? 'bg-gray-100 text-gray-700' :
                    c.status === 'PRINTED' ? 'bg-blue-100 text-blue-700' :
                    c.status === 'VERIFIED' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {c.status}
                  </div>
                  <div className="flex gap-1 print:hidden">
                    <button
                      title="Copy URL"
                      onClick={() => copy(c.traceUrl)}
                      className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                    <button
                      title="Download PNG"
                      onClick={() => downloadAsPng(c.traceUrl, c.traceCode)}
                      className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end print:hidden">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ITraceQRViewerModal;
